import { memo, useCallback } from 'react';

import { type FiltersTrackingLocation } from '@air/analytics';
import { ChevronDownIcon, PlusIcon, XIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import {
  ui2026FilterChip,
  ui2026FilterChipClose,
  ui2026MoreFiltersIdle,
} from '~/components/Filters/FiltersBar/filtersBarUi2026';
import {
  FiltersDropdownTrigger,
  type FiltersDropdownTriggerProps,
} from '~/components/Filters/FiltersButton/FiltersDropdownTrigger';
import { useFiltersDropdownController } from '~/components/Filters/FiltersDropdown/useFiltersDropdownController';
import {
  PublicFiltersDropdown,
  type PublicFiltersDropdownProps,
} from '~/components/Filters/v2/components/PublicFiltersDropdown';
import { type CustomFieldFilterType, type FilterType } from '~/components/FiltersMenu/types';
import { useFiltersCountAppliedFromDropdown } from '~/hooks/filters/useFiltersCountAppliedFromDropdown';

export type PublicMoreFiltersTriggerProps = {
  filters: FilterType[];
  customFields: CustomFieldFilterType[];
  trackLocation: FiltersTrackingLocation;
} & Pick<PublicFiltersDropdownProps, 'availableTypes' | 'disabledFilters' | 'otherFilters'>;

export const PublicMoreFiltersTrigger = memo(
  ({
    filters,
    customFields,
    availableTypes,
    otherFilters,
    disabledFilters,
    trackLocation,
  }: PublicMoreFiltersTriggerProps) => {
    const { filtersCount } = useFiltersCountAppliedFromDropdown({
      filtersToCount: filters,
      customFieldsToCount: customFields,
    });
    const { clearSelectedFilters } = useFiltersDropdownController({
      availableFilters: filters,
      customFields,
      trackLocation,
    });
    const renderFiltersDropdown: FiltersDropdownTriggerProps['renderFiltersDropdown'] = useCallback(
      ({ closePopover }) => {
        return (
          <PublicFiltersDropdown
            availableTypes={availableTypes}
            otherFilters={otherFilters}
            availableFilters={filters}
            customFields={customFields}
            title="More filters"
            onApplyClick={closePopover}
            trackLocation={trackLocation}
            disabledFilters={disabledFilters}
          />
        );
      },
      [availableTypes, customFields, disabledFilters, filters, otherFilters, trackLocation],
    );

    const renderTrigger: FiltersDropdownTriggerProps['renderTrigger'] = useCallback(
      ({ isActive, onClick }) => {
        const countBadge = filtersCount ? (
          <span
            className="mr-1.5 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-24 bg-blue-11 ui2026:bg-altitude-9 px-1 text-center font-bold text-10 text-blue-1 ui2026:text-white"
            data-testid="MORE_FILTERS_TRIGGER_COUNT"
          >
            {filtersCount}
          </span>
        ) : null;

        return (
          <div className="group relative shrink-0">
            <Button
              appearance={isActive || filtersCount ? 'outline' : 'ghost'}
              color={isActive || filtersCount ? 'blue' : 'grey'}
              className={cn(
                filtersCount ? 'pr-5' : 'pr-2',
                isActive || filtersCount ? ui2026FilterChip({ isActive: true }) : ui2026MoreFiltersIdle(),
              )}
              data-testid="MORE_FILTERS_TRIGGER"
              onClick={onClick}
              prefix={!filtersCount && <PlusIcon className="shrink-0" color="currentColor" size="xs" />}
              size="small"
              suffix={!filtersCount && <ChevronDownIcon className="shrink-0" color="currentColor" size="xs" />}
            >
              {countBadge}
              More
            </Button>
            {!!filtersCount && (
              <IconButton
                appearance="ghost"
                className={cn('absolute top-1/2 right-1 z-1 -translate-y-1/2', ui2026FilterChipClose())}
                color={isActive || filtersCount ? 'blue' : 'grey'}
                data-testid="MORE_FILTERS_TRIGGER_CLOSE"
                adsIcon
                icon={XIcon}
                label="Clear filters"
                onClick={clearSelectedFilters}
                size="extra-small"
              />
            )}
          </div>
        );
      },
      [clearSelectedFilters, filtersCount],
    );

    return <FiltersDropdownTrigger renderFiltersDropdown={renderFiltersDropdown} renderTrigger={renderTrigger} />;
  },
);

PublicMoreFiltersTrigger.displayName = 'PublicMoreFiltersTrigger';
